/**
 * Authenticated API service that includes bearer tokens
 */

import { getApiBaseUrl } from '../utils/apiConfig';
import { getAuthToken } from './firebase';
import { afterUnauthorized, signOutOfBothProjects } from '../components/Auth/authProject';

// Function to create authenticated fetch options (with fresh Firebase token)
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Generic authenticated fetch with automatic token refresh on 401
export const authenticatedFetch = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const url = `${getApiBaseUrl()}${endpoint}`;

  const send = async () =>
    fetch(url, {
      ...options,
      headers: {
        ...(await getAuthHeaders()),
        ...options.headers,
      },
    });

  const response = await send();

  // On 401, one shared handover or token refresh, then retry once. Callers are
  // public pages that handle a 401 themselves, so this never navigates.
  if (response.status === 401) {
    const verdict = await afterUnauthorized(await response.clone().json().catch(() => null), false);
    if (verdict === 'retry') return send();
    if (verdict === 'signin-moved') await signOutOfBothProjects();
    localStorage.removeItem('bearer_token');
  }

  return response;
};

// Typed fetch functions for common HTTP methods
export const authGet = async (endpoint: string): Promise<any> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const authPost = async (endpoint: string, data?: any): Promise<any> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const authPut = async (endpoint: string, data?: any): Promise<any> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const authDelete = async (endpoint: string): Promise<any> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};